import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { MailIcon, CheckCircleIcon, ClockIcon, RefreshCwIcon, LogInIcon } from 'lucide-react';
import axios from 'axios';

const EmailVerificationPending = () => {
  const location = useLocation();
  const email = location.state?.email || '';
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleResendEmail = async () => {
    setResending(true);
    setResendMessage('');
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, { email });
      setResendMessage('✅ Doğrulama e-postası tekrar gönderildi! Lütfen e-posta kutunuzu kontrol edin.');
    } catch (error) {
      setResendMessage('❌ E-posta gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full p-8 md:p-12 shadow-2xl">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center animate-pulse">
              <MailIcon className="w-12 h-12 text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
              <ClockIcon className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          📧 E-posta Doğrulama Bekleniyor
        </h1>

        {/* Professional Message */}
        <div className="space-y-4 text-center mb-8">
          <p className="text-lg text-gray-700 leading-relaxed">
            Merhaba! <strong>AdvanceCash.io</strong>'ya hoş geldiniz. 🎉
          </p>
          
          <p className="text-gray-600 leading-relaxed">
            E-posta adresinizi doğrulamak için <strong className="text-blue-600">{email}</strong> adresine 
            bir doğrulama bağlantısı gönderdik.
          </p>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-4 rounded-lg my-6">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="text-blue-700">📌 Sonraki Adımlar:</strong>
            </p>
            <ol className="text-left text-sm text-gray-600 mt-2 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">1.</span>
                E-posta kutunuzu açın ve "AdvanceCash.io" gönderisini bulun
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">2.</span>
                E-postadaki <strong>"E-postamı Doğrula"</strong> butonuna tıklayın
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">3.</span>
                Doğrulama sonrası otomatik olarak <strong>ödeme sayfasına</strong> yönlendirileceksiniz
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">4.</span>
                Ödeme işleminizi tamamlayın ve platformu kullanmaya başlayın!
              </li>
            </ol>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
            <div className="flex items-start">
              <ClockIcon className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                <strong>Önemli:</strong> Doğrulama bağlantısı <strong>24 saat</strong> geçerlidir.
              </p>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600 mb-2">
            <strong>💡 E-posta gelmedi mi?</strong>
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4">
            <li>• Spam/Gereksiz klasörünü kontrol edin</li>
            <li>• E-posta adresinizi doğru yazdığınızdan emin olun</li>
            <li>• 2-3 dakika bekleyin, bazen gecikme olabilir</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Button
            onClick={handleResendEmail}
            disabled={resending}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50 w-full sm:w-auto"
          >
            {resending ? (
              <>
                <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
                Gönderiliyor...
              </>
            ) : (
              <>
                <RefreshCwIcon className="w-4 h-4 mr-2" />
                E-postayı Tekrar Gönder
              </>
            )}
          </Button>

          <Link to="/login" className="w-full sm:w-auto">
            <Button
              variant="default"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white w-full"
            >
              <LogInIcon className="w-4 h-4 mr-2" />
              Giriş Yap
            </Button>
          </Link>
        </div>

        {resendMessage && (
          <p className={`mt-4 text-center text-sm ${resendMessage.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
            {resendMessage}
          </p>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            Sorularınız mı var? 
            <a href="mailto:destek@advancecash.io" className="text-blue-600 hover:underline ml-1">
              destek@advancecash.io
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default EmailVerificationPending;
